import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PrinterIcon, EyeIcon } from '@heroicons/react/24/outline';
import axiosInstance from '../../api/axiosConfig';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { Link } from 'react-router-dom';

// Interface based on existing Structure, assuming it will be expanded
// with imageUrl and coordinates later.
interface Structure {
  id: number;
  code: string;
  type: string;
  name: string;
  address: string;
  size: string;
  currentDailyPrice: number | null;
  currentMonthlyPrice: number | null;
  isActive: boolean;
  imageUrl?: string; // For future use
  latitude?: number; // For future use
  longitude?: number; // For future use
}

const NewPriceListPage: React.FC = () => {
  const navigate = useNavigate();

  const [allStructures, setAllStructures] = useState<Structure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStructureIds, setSelectedStructureIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    const fetchStructures = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosInstance.get<Structure[]>('/structures/active'); // Endpoint from original PriceList
        setAllStructures(response.data);
        // Initially select all structures
        setSelectedStructureIds(new Set(response.data.map(s => s.id)));
      } catch (err) {
        console.error('Error fetching structures:', err);
        setError('Ошибка при загрузке данных о конструкциях');
        setAllStructures([]);
      } finally {
        setLoading(false);
      }
    };
    fetchStructures();
  }, []);

  const groupedStructures = useMemo(() => {
    return allStructures.reduce((acc, structure) => {
      const type = structure.type || 'Uncategorized';
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(structure);
      return acc;
    }, {} as Record<string, Structure[]>);
  }, [allStructures]);

  const handleSelectStructure = (structureId: number) => {
    setSelectedStructureIds(prevSelectedIds => {
      const newSelectedIds = new Set(prevSelectedIds);
      if (newSelectedIds.has(structureId)) {
        newSelectedIds.delete(structureId);
      } else {
        newSelectedIds.add(structureId);
      }
      return newSelectedIds;
    });
  };

  const handleSelectAllStructuresInType = (type: string, structuresInGroup: Structure[]) => {
    const allIdsInGroup = new Set(structuresInGroup.map(s => s.id));
    const areAllCurrentlySelectedInGroup = structuresInGroup.every(s => selectedStructureIds.has(s.id));

    setSelectedStructureIds(prevSelectedIds => {
      const newSelectedIds = new Set(prevSelectedIds);
      if (areAllCurrentlySelectedInGroup) {
        allIdsInGroup.forEach(id => newSelectedIds.delete(id));
      } else {
        allIdsInGroup.forEach(id => newSelectedIds.add(id));
      }
      return newSelectedIds;
    });
  };
  
  const handleSelectAllToggle = () => {
    if (selectedStructureIds.size === allStructures.length) {
      setSelectedStructureIds(new Set()); // Deselect all
    } else {
      setSelectedStructureIds(new Set(allStructures.map(s => s.id))); // Select all
    }
  };

  const handlePrintSelected = () => {
    if (selectedStructureIds.size === 0) {
      setError("Пожалуйста, выберите хотя бы одну конструкцию для печати.");
      return;
    }
    const params = new URLSearchParams();
    params.append('ids', Array.from(selectedStructureIds).join(','));
    navigate(`/structures/new-price-list/print?${params.toString()}`);
  };
  
  const formatCurrency = (amount: number | null): string => {
    if (amount === null || isNaN(amount)) return '-';
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const shouldShowDailyPrice = (structureType: string): boolean => {
    return structureType !== 'Ситиборд';
  };

  const shouldShowDailyPriceForGroup = (structures: Structure[]): boolean => {
    return structures.some(structure => shouldShowDailyPrice(structure.type));
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <PageHeader
        title="Новый Прайс-лист (Выбор)"
        subtitle="Выберите конструкции для печатной версии"
        backLink="/structures"
        actions={
          <button
            onClick={handlePrintSelected}
            className="btn-primary flex items-center"
            disabled={loading || selectedStructureIds.size === 0}
          >
            <PrinterIcon className="h-5 w-5 mr-1" />
            Печатная версия
          </button>
        }
      />

      {error && (
        <div className="bg-red-100 text-red-700 p-4 rounded-md mb-4">
          {error}
        </div>
      )}

      <div className="mb-4 p-4 bg-white rounded shadow">
        <button
            onClick={handleSelectAllToggle}
            className="btn-secondary mb-4"
            disabled={allStructures.length === 0}
        >
            {selectedStructureIds.size === allStructures.length ? 'Снять выделение со всех' : 'Выделить все'} ({selectedStructureIds.size} / {allStructures.length})
        </button>
      </div>

      {Object.keys(groupedStructures).length === 0 && !loading ? (
        <div className="text-center p-6 bg-white rounded-lg shadow">
          <p className="text-gray-500">Нет активных конструкций для отображения.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedStructures).map(([type, structuresInGroup]) => {
            const areAllSelectedInGroup = structuresInGroup.every(s => selectedStructureIds.has(s.id));
            const showDailyPriceForGroup = shouldShowDailyPriceForGroup(structuresInGroup);
            
            return (
              <div key={type} className="bg-white rounded-lg shadow">
                <header className="p-4 border-b border-gray-200 flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-800">{type} ({structuresInGroup.length})</h2>
                  <button
                    onClick={() => handleSelectAllStructuresInType(type, structuresInGroup)}
                    className="btn-outline text-sm"
                    disabled={structuresInGroup.length === 0}
                  >
                    {areAllSelectedInGroup ? 'Снять выделение с группы' : 'Выделить всю группу'}
                  </button>
                </header>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">Выбор</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Код</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Название</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Адрес</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Размер</th>
                        {showDailyPriceForGroup && (
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Цена/день</th>
                        )}
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Цена/месяц</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Детали</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {structuresInGroup.map((structure) => (
                        <tr key={structure.id} className={`${selectedStructureIds.has(structure.id) ? 'bg-blue-50' : ''}`}>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              className="form-checkbox h-5 w-5 text-primary focus:ring-primary-dark border-gray-300 rounded"
                              checked={selectedStructureIds.has(structure.id)}
                              onChange={() => handleSelectStructure(structure.id)}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{structure.code}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{structure.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{structure.address}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{structure.size}</td>
                          {showDailyPriceForGroup && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                              {shouldShowDailyPrice(structure.type) 
                                ? formatCurrency(structure.currentDailyPrice)
                                : '-'}
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">{formatCurrency(structure.currentMonthlyPrice)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                            <Link
                              to={`/structures/${structure.id}`}
                              className="text-primary hover:text-primary-dark p-1"
                              title="Детали конструкции"
                            >
                              <EyeIcon className="h-5 w-5 inline-block" />
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NewPriceListPage;